import importedWx from 'weixin-js-sdk'
import saleServiceApi from '@/api/sale_service_api'
import { buildShareMeta, isWechatBrowser, pickWechatSDK } from './wechatShare'

/**
 * 微信分享卡片的「组装」层：拿签名 → wx.config → 设分享数据。
 *
 * 两条刻意的取舍：
 * - 分享是"锦上添花"：任何一步失败只打 console.warn，绝不影响页面本身；
 * - 签名配置只取一次并缓存（hash 路由切页不会改变 href.split('#')[0]，
 *   签名不会因为切页面失效），但分享数据每次切页都重设。
 */

// 新版两个 + 旧版两个：旧微信（6.x 安卓）只认 onMenuShare*，不配的话那批机器分享出来是纯链接
const SHARE_API_LIST = [
	'updateAppMessageShareData',
	'updateTimelineShareData',
	'onMenuShareAppMessage',
	'onMenuShareTimeline'
]

let configPromise = null

function shareImgUrl() {
	// 微信要求绝对地址；icon.png 是应用默认图标
	return new URL('/static/imgs/icon.png', window.location.origin).href
}

async function ensureWechatConfig(appId) {
	if (configPromise) return configPromise
	configPromise = (async () => {
		const wxSDK = pickWechatSDK([importedWx, window.jWeixin, window.wx])
		if (!wxSDK) throw new Error('微信 SDK 未加载')

		const pageUrl = encodeURI(window.location.href.split('#')[0])
		const res = await saleServiceApi.wechatJssdk(appId, pageUrl)
		if (!res || res.errcode !== 0) throw new Error((res && res.errmsg) || '获取微信签名失败')
		let config = res.other
		if (typeof config === 'string') {
			try { config = JSON.parse(config) } catch (e) { throw new Error('微信签名解析失败') }
		}

		await new Promise((resolve, reject) => {
			wxSDK.ready(() => resolve())
			wxSDK.error(err => reject(new Error((err && (err.errMsg || err.err_msg)) || '微信签名校验失败')))
			wxSDK.config({
				debug: false,
				appId: config.app_id || config.appId,
				timestamp: config.timestamp,
				nonceStr: config.nonce_str || config.nonceStr,
				signature: config.signature,
				jsApiList: SHARE_API_LIST
			})
		})
		return wxSDK
	})().catch(err => {
		// 失败就把缓存清掉，下次重进页面还能再试；缓存住失败态 = 这次进微信永远没分享卡片
		configPromise = null
		throw err
	})
	return configPromise
}

/**
 * 设置当前页的分享卡片。不在微信里/没 appId 时直接不做事。
 * @param appId     公众号 app_id
 * @param routePath 当前路由 path（决定分享标题用页面名还是品牌名）
 */
export async function setupWechatShare(appId, routePath) {
	if (!appId || !isWechatBrowser()) return
	try {
		const wxSDK = await ensureWechatConfig(appId)
		const meta = buildShareMeta(routePath, document.title)
		const payload = {
			title: meta.title,
			desc: meta.desc,
			link: window.location.href,
			imgUrl: shareImgUrl()
		}
		if (typeof wxSDK.updateAppMessageShareData === 'function') wxSDK.updateAppMessageShareData(payload)
		if (typeof wxSDK.updateTimelineShareData === 'function') {
			wxSDK.updateTimelineShareData({ title: payload.title, link: payload.link, imgUrl: payload.imgUrl })
		}
		if (typeof wxSDK.onMenuShareAppMessage === 'function') wxSDK.onMenuShareAppMessage(payload)
		if (typeof wxSDK.onMenuShareTimeline === 'function') {
			wxSDK.onMenuShareTimeline({ title: payload.title, link: payload.link, imgUrl: payload.imgUrl })
		}
	} catch (e) {
		console.warn('微信分享配置失败', e)
	}
}
